import {input} from '../input/InputManager.js';
import {capturePointer} from '../input/pointerCapture.js';
import {layoutKey, mobileControls, mobileControlVisible, moveControl, normalizeLayouts, orientationFor, placeControl, readMobileLayouts, saveMobileLayouts} from '../input/mobileLayout.js';
import {Settings} from '../Settings';
import {localize} from '../utils/Localization';
import {reloadLayout} from '../utils/MobileController';
import {MobileControlView} from './MobileControlView.js';
import '../css/MobileControlsPanel.scss';

export class MobileControlsPanel {
    constructor(container, onBack) {
        this.onBack = onBack;
        this.pointers = new Map();
        this.mode = 'vehicle';
        this.element = document.createElement('div');
        this.element.className = 'mobile-controls-panel';
        this.element.hidden = true;
        this.element.setAttribute('role', 'dialog');
        this.element.setAttribute('aria-modal', 'true');
        this.element.setAttribute('aria-labelledby', 'mobile-controls-title');
        this.element.innerHTML = `
            <div class="mobile-controls-tools">
                <h2 id="mobile-controls-title">${localize('controls_title')}</h2>
                <div class="mobile-layout-tabs" role="tablist" aria-label="${localize('controls_mobile_orientation')}">
                    <button type="button" role="tab" data-orientation="portrait">${localize('controls_mobile_portrait')}</button>
                    <button type="button" role="tab" data-orientation="landscape">${localize('controls_mobile_landscape')}</button>
                </div>
                <div class="mobile-mode-tabs" role="group" aria-label="${localize('controls_mobile_preview')}">
                    <button type="button" data-mode="vehicle">${localize('controls_mobile_vehicle')}</button>
                    <button type="button" data-mode="character">${localize('controls_mobile_character')}</button>
                    <button type="button" data-mode="runner">${localize('controls_mobile_runner')}</button>
                </div>
                <p class="mobile-controls-help">${localize('controls_mobile_help')}</p>
            </div>
            <div class="mobile-controls-workspace">
                <div class="mobile-preview-frame"><div class="mobile-preview-screen"></div></div>
            </div>
            <div class="mobile-controls-footer">
                <p class="mobile-selection" role="status" aria-live="polite"></p>
                <button type="button" class="mobile-reset">${localize('controls_reset')}</button>
                <div class="mobile-controls-actions">
                    <button type="button" class="mobile-cancel">${localize('controls_cancel')}</button>
                    <button type="button" class="mobile-save">${localize('controls_mobile_save')}</button>
                </div>
            </div>`;
        container.appendChild(this.element);
        this.workspace = this.element.querySelector('.mobile-controls-workspace');
        this.frame = this.element.querySelector('.mobile-preview-frame');
        this.screen = this.element.querySelector('.mobile-preview-screen');
        this.screen.setAttribute('role', 'group');
        this.screen.setAttribute('aria-label', localize('controls_mobile_preview'));
        this.view = new MobileControlView(this.screen, Settings.touchButtonSize);
        this.view.jumpEnabled = true;
        this.view.controls.levelControls.textContent = localize('controls_level_title');
        this.handles = {};
        for (const [id, label] of Object.entries(mobileControls)) {
            const handle = document.createElement('button');
            handle.type = 'button';
            handle.className = `mobile-control-handle${id === 'dpad' ? ' is-dpad' : ''}`;
            handle.dataset.mobileHandle = id;
            handle.setAttribute('aria-label', localize(label));
            handle.onfocus = () => this.select(id);
            this.screen.appendChild(handle);
            this.handles[id] = handle;
        }
        this.element.querySelectorAll('[data-orientation]').forEach(button => button.onclick = () => {
            this.endGesture(); this.orientation = button.dataset.orientation; this.resize();
        });
        this.element.querySelectorAll('[data-mode]').forEach(button => button.onclick = () => {
            this.endGesture(); this.mode = button.dataset.mode; this.render();
        });
        this.element.querySelector('.mobile-reset').onclick = () => {
            this.endGesture(); this.draft[this.orientation] = {}; this.render();
        };
        this.element.querySelector('.mobile-cancel').onclick = () => this.close();
        this.element.querySelector('.mobile-save').onclick = () => {
            this.endGesture(); saveMobileLayouts(this.draft);
            reloadLayout(); this.close();
        };
        this.workspace.addEventListener('pointerdown', event => this.pointerDown(event));
        this.workspace.addEventListener('pointermove', event => this.pointerMove(event));
        for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
            this.workspace.addEventListener(type, event => this.pointerUp(event));
        }
        // Touch events also reach the game's document listeners on mobile.
        for (const type of ['touchstart', 'touchmove', 'touchend', 'touchcancel', 'contextmenu']) {
            this.workspace.addEventListener(type, event => { event.preventDefault(); event.stopPropagation(); }, {passive: false});
        }
        window.addEventListener('keydown', event => this.keydown(event), true);
        window.addEventListener('keyup', event => { if (!this.element.hidden) event.stopImmediatePropagation(); }, true);
        window.addEventListener('blur', () => this.endGesture());
        document.addEventListener('visibilitychange', () => { if (document.hidden) this.endGesture(); });
        this.observer = new ResizeObserver(() => { if (!this.element.hidden) this.resize(); });
        this.observer.observe(this.workspace);
    }
    show() {
        input.setBlocked(true);
        this.draft = normalizeLayouts(readMobileLayouts());
        this.orientation = this.deviceOrientation = orientationFor(window.innerWidth, window.innerHeight);
        this.selected = null;
        this.element.hidden = false;
        this.resize();
    }
    hide() { this.endGesture(); this.element.hidden = true; }
    close() { this.hide(); this.onBack(); }
    resize() {
        this.endGesture();
        const current = orientationFor(window.innerWidth, window.innerHeight);
        if (current !== this.deviceOrientation) this.orientation = current;
        this.deviceOrientation = current;
        const short = Math.min(window.innerWidth, window.innerHeight), long = Math.max(window.innerWidth, window.innerHeight);
        const width = this.orientation === 'landscape' ? long : short;
        const height = this.orientation === 'landscape' ? short : long;
        this.zoom = Math.min((this.workspace.clientWidth - 16) / width, (this.workspace.clientHeight - 16) / height, 1);
        if (this.zoom <= 0) return;
        this.screen.style.width = `${width}px`;
        this.screen.style.height = `${height}px`;
        this.screen.style.transform = `scale(${this.zoom})`;
        this.frame.style.width = `${width * this.zoom}px`;
        this.frame.style.height = `${height * this.zoom}px`;
        this.view.resize(width, height, this.orientation, this.draft[this.orientation]);
        this.render();
    }
    render() {
        this.view.setMode(this.mode, this.draft[this.orientation]);
        this.element.querySelectorAll('[data-orientation]').forEach(button => button.setAttribute('aria-selected', String(button.dataset.orientation === this.orientation)));
        this.element.querySelectorAll('[data-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mode === this.mode)));
        for (const [id, handle] of Object.entries(this.handles)) {
            const box = this.view.geometry[id];
            handle.hidden = !mobileControlVisible(id, this.mode);
            // A small icon still has a usable touch target in a scaled preview.
            const width = Math.max(box.width, 44 / this.zoom), height = Math.max(box.height, 44 / this.zoom);
            Object.assign(handle.style, {left: `${box.x - width / 2}px`, top: `${box.y - height / 2}px`,
                width: `${width}px`, height: `${height}px`});
        }
        if (this.selected && this.handles[this.selected].hidden) this.selected = null;
        this.select(this.selected);
    }
    select(id) {
        this.selected = id;
        for (const [key, handle] of Object.entries(this.handles)) handle.classList.toggle('selected', key === id);
        this.element.querySelector('.mobile-selection').textContent = id
            ? localize('controls_mobile_selected', {action: localize(mobileControls[id]), size: Math.round(this.view.geometry[id].scale * 100)})
            : localize('controls_mobile_separate');
    }
    point(event) {
        const rect = this.screen.getBoundingClientRect();
        return {x: (event.clientX - rect.left) / this.zoom, y: (event.clientY - rect.top) / this.zoom};
    }
    pointerDown(event) {
        if (event.button !== 0 || this.pointers.size >= 2) return;
        if (!this.pointers.size) {
            const id = event.target.closest('[data-mobile-handle]')?.dataset.mobileHandle;
            if (!id) return;
            this.select(id);
        }
        event.preventDefault(); event.stopPropagation();
        capturePointer(this.workspace, event.pointerId);
        this.pointers.set(event.pointerId, this.point(event));
        this.rebase();
    }
    rebase() {
        this.gesture = this.pointers.size ? {geometry: {...this.view.geometry[this.selected]}, points: [...this.pointers.values()]} : null;
    }
    pointerMove(event) {
        if (!this.pointers.has(event.pointerId)) return;
        event.preventDefault(); event.stopPropagation();
        this.pointers.set(event.pointerId, this.point(event));
        this.draft[this.orientation][layoutKey(this.selected, this.mode)] = moveControl(this.gesture, [...this.pointers.values()], this.view.bases[this.selected], this.view.viewport);
        this.render();
    }
    pointerUp(event) {
        if (!this.pointers.delete(event.pointerId)) return;
        event.preventDefault(); event.stopPropagation();
        if (this.workspace.hasPointerCapture(event.pointerId)) this.workspace.releasePointerCapture(event.pointerId);
        this.rebase();
    }
    endGesture() {
        const ids = [...this.pointers.keys()];
        this.pointers.clear(); this.gesture = null;
        for (const id of ids) if (this.workspace.hasPointerCapture(id)) this.workspace.releasePointerCapture(id);
    }
    keydown(event) {
        if (this.element.hidden) return;
        event.stopImmediatePropagation();
        if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
        if (event.key === 'Tab') {
            const buttons = [...this.element.querySelectorAll('button')].filter(button => !button.hidden);
            const index = buttons.indexOf(document.activeElement);
            if (event.shiftKey && index <= 0) { event.preventDefault(); buttons.at(-1).focus(); }
            else if (!event.shiftKey && (index < 0 || index === buttons.length - 1)) { event.preventDefault(); buttons[0].focus(); }
            return;
        }
        const id = document.activeElement?.dataset.mobileHandle;
        const direction = {ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1]}[event.key];
        if (id && direction) {
            event.preventDefault();
            const box = this.view.geometry[id], step = event.shiftKey ? 1 : 10;
            this.draft[this.orientation][layoutKey(id, this.mode)] = placeControl(this.view.bases[id], {x: box.x + direction[0] * step, y: box.y + direction[1] * step}, box.scale, this.view.viewport);
            this.render();
        }
    }
}
